import React, { useState, useEffect } from 'react';
import './Login.css';
import LoginConnecting from './LoginConnecting';
import ThemeSwitcher from './Components/ThemeSwitcher';

function Login(prop) {
    const [register, setRegister] = useState(false);
    const [currentTheme, setCurrentTheme] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        return savedTheme || 'default';
    });

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', currentTheme);
        localStorage.setItem('theme', currentTheme);
    }, [currentTheme]);

    const handleThemeChange = (theme) => {
        setCurrentTheme(theme);
    };

    const handleLogin = (e, name, password) => {
        e.preventDefault();
        prop.login(name, password);
    }

    const handleRegister = (e, name, password, email) => {
        e.preventDefault();
        prop.register(name, password, email);
    }

    if (prop.connecting) {
        return <LoginConnecting connecting={prop.connecting} connected={prop.connected} register={register} loggedIn={prop.loggedIn} />;
    }

    return (
        <>
            <ThemeSwitcher currentTheme={currentTheme} onThemeChange={handleThemeChange} />
            <div className='wrapper'>
                {register ?
                    <RegistrationForm handleRegister={handleRegister} setRegister={setRegister} />
                    :
                    <LoginForm handleLogin={handleLogin} setRegister={setRegister} />
                }
            </div>
        </>
    );
}

function LoginForm({ handleLogin, setRegister }) {
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await handleLogin(e, name, password);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form className='form' onSubmit={handleSubmit} noValidate>
            <div className="login-header">
                <h1>Welcome Back</h1>
                <p>Please sign in to your account to continue</p>
            </div>
            <div className="form-group">
                <label htmlFor='username'>Account</label>
                <input 
                    id="username"
                    className="username" 
                    type="text" 
                    placeholder='Enter your account name'
                    onChange={(e) => setName(e.target.value)} 
                    value={name} 
                    autoFocus 
                    required
                    aria-required="true"
                    aria-label="Account name"
                />
            </div>
            <div className="form-group">
                <label htmlFor='password'>Password</label>
                <div className="password-input-container">
                    <input 
                        id="password"
                        className="password" 
                        type="password"
                        placeholder='Enter your password'
                        onChange={(e) => setPassword(e.target.value)} 
                        value={password}
                        required
                        aria-required="true"
                        aria-label="Password"
                    />
                </div>
            </div>
            <button 
                className="sign" 
                type="submit"
                aria-busy={isLoading}
                disabled={isLoading || !name || !password}
            >
                {isLoading ? "Signing in..." : "Sign in"}
            </button>
            <button 
                className='register' 
                type="button"
                onClick={() => setRegister(true)}
            >
                Create an account
            </button>
            <a href="#forgot-password" onClick={(e) => {
                e.preventDefault();
                // Handle forgot password
            }}>
                Forgotten password?
            </a>
        </form>
    );
}

function RegistrationForm({ handleRegister, setRegister }) {
    const [account, setAccount] = useState("");
    const [password, setPassword] = useState("");
    const [password2, setPassword2] = useState("");
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};
        
        if (account.length < 4 || account.length > 18) {
            newErrors.account = "Account name must be between 4 and 18 characters";
        }
        
        if (password.length < 8) {
            newErrors.password = "Password must be at least 8 characters long";
        }
        
        if (password !== password2) {
            newErrors.password2 = "Passwords do not match";
        }
        
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = "Please enter a valid email address";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        
        setIsLoading(true);
        try {
            await handleRegister(e, account, password, email);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className='form' noValidate>
            <div className="login-header">
                <h1>Create Account</h1>
                <p>Join our community and start your adventure</p>
            </div>
            <div className="form-group">
                <label htmlFor='reg-username'>Account</label>
                <input 
                    id="reg-username"
                    className="username" 
                    type="text" 
                    placeholder='Choose an account name'
                    onChange={(e) => setAccount(e.target.value)} 
                    value={account}
                    required
                    aria-required="true"
                    aria-label="Account name"
                    aria-invalid={!!errors.account}
                    aria-describedby={errors.account ? "account-error" : undefined}
                />
                {errors.account && (
                    <span id="account-error" className="error-text" role="alert">
                        {errors.account}
                    </span>
                )}
            </div>
            <div className="form-group">
                <label htmlFor='reg-email'>Email (Optional)</label>
                <input 
                    id="reg-email"
                    className="email" 
                    type="email" 
                    placeholder='you@example.com'
                    onChange={(e) => setEmail(e.target.value)} 
                    value={email}
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "email-error" : undefined}
                />
                {errors.email && (
                    <span id="email-error" className="error-text" role="alert">
                        {errors.email}
                    </span>
                )}
            </div>
            <div className="form-group">
                <label htmlFor='reg-password'>Password</label>
                <div className="password-input-container">
                    <input 
                        id="reg-password"
                        className="password" 
                        type="password"
                        placeholder='Create a password'
                        onChange={(e) => setPassword(e.target.value)} 
                        value={password}
                        required
                        aria-required="true"
                        aria-label="Password"
                        aria-invalid={!!errors.password}
                        aria-describedby={errors.password ? "password-error" : undefined}
                    />
                </div>
                {errors.password && (
                    <span id="password-error" className="error-text" role="alert">
                        {errors.password}
                    </span>
                )}
            </div>
            <div className="form-group">
                <label htmlFor='reg-password2'>Verify Password</label>
                <div className="password-input-container">
                    <input 
                        id="reg-password2"
                        className="verify-password" 
                        type="password"
                        placeholder='Confirm your password'
                        onChange={(e) => setPassword2(e.target.value)} 
                        value={password2}
                        required
                        aria-required="true"
                        aria-label="Verify password"
                        aria-invalid={!!errors.password2}
                        aria-describedby={errors.password2 ? "password2-error" : undefined}
                    />
                </div>
                {errors.password2 && (
                    <span id="password2-error" className="error-text" role="alert">
                        {errors.password2}
                    </span>
                )}
            </div>
            <button 
                className="sign" 
                type="submit"
                aria-busy={isLoading}
                disabled={isLoading || !account || !password || !password2}
            >
                {isLoading ? "Creating account..." : "Register account"}
            </button>
            <button 
                className="register" 
                type="button"
                onClick={() => setRegister(false)}
            >
                Back to login
            </button>
        </form>
    );
}

export default Login;